import { DomainValidationError } from "./identifiers.js";
import { sha256HexOfText } from "./sha256.js";
// The deterministic Stream ids of Milestone C (docs/architecture/milestone-c-contracts.md section 3.1; ADR 0009). Every id is
// built from a namespace, a fixed word and a Lafayette uuid. No id holds a name, an email, a phone number or a Supabase subject.
// Dev and staging share one Stream app, so the namespace is the ONLY thing that keeps their customers apart: every builder
// below takes it, and every parser returns it, so a caller can refuse a foreign namespace.
//
// `@lafayette/schemas` repeats these patterns for the clients (`messaging.ts`); a parity test in `@lafayette/agent-contract`
// fails when the two differ.
/** `dev`, `stg`, `prd`, `tst`, or an operator override such as `devsk`. Lowercase, 2 to 8 characters. */
export const MESSAGING_NAMESPACE_SHAPE = /^[a-z][a-z0-9]{1,7}$/;
/** The default namespace of each environment. */
export const DEFAULT_MESSAGING_NAMESPACE_BY_ENVIRONMENT = Object.freeze({
    development: "dev",
    test: "tst",
    staging: "stg",
    production: "prd",
});
const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const UUID_SHAPE = new RegExp(`^${UUID}$`);
const STREAM_USER_SHAPE = new RegExp(`^(?<ns>[a-z][a-z0-9]{1,7})-(?:(?<kind>cust|staff)-(?<id>${UUID})|lafayette-(?<actor>ai|system|copilot))$`);
const CUSTOMER_CHANNEL_SHAPE = new RegExp(`^(?<ns>[a-z][a-z0-9]{1,7})-conv-(?<id>${UUID})$`);
const COPILOT_CHANNEL_SHAPE = /^(?<ns>[a-z][a-z0-9]{1,7})-cop-(?<hash>[0-9a-f]{32})$/;
const MESSAGE_SHAPE = new RegExp(`^(?:(?<prefix>lfai|lfsys|lfcop)-)?(?<id>${UUID})$`);
/** Validates the shape of a namespace. The message names the rule and never the value. */
export function messagingNamespace(value) {
    if (typeof value !== "string" || !MESSAGING_NAMESPACE_SHAPE.test(value)) {
        throw new DomainValidationError("messagingNamespace", "must be 2 to 8 lowercase letters or digits and start with a letter");
    }
    return value;
}
/**
 * The namespace of an environment. `override` (`LAFAYETTE_STREAM_NAMESPACE`) is allowed in development only (per-developer
 * namespaces); staging, production and test refuse it, so two environments can never be pointed at one namespace by accident.
 * The reserved namespaces (`dev`, `tst`, `stg`, `prd`) are refused as overrides unless they are the namespace of the environment.
 */
export function resolveMessagingNamespace(input) {
    if (input.override === undefined) {
        return messagingNamespace(DEFAULT_MESSAGING_NAMESPACE_BY_ENVIRONMENT[input.environment]);
    }
    if (input.environment !== "development") {
        throw new DomainValidationError("messagingNamespace", "cannot be overridden outside development");
    }
    const namespace = messagingNamespace(input.override);
    // The four default namespaces are reserved: a development override equal to another environment's namespace would issue its
    // identities and reuse its channels in the shared Stream app (for example a staging database copy on a developer machine).
    const own = DEFAULT_MESSAGING_NAMESPACE_BY_ENVIRONMENT[input.environment];
    if (namespace !== own &&
        Object.values(DEFAULT_MESSAGING_NAMESPACE_BY_ENVIRONMENT).includes(namespace)) {
        throw new DomainValidationError("messagingNamespace", "is reserved for another environment and cannot be used as an override");
    }
    return namespace;
}
function uuidOf(field, value) {
    if (typeof value !== "string" || !UUID_SHAPE.test(value)) {
        throw new DomainValidationError(field, "must be a canonical lowercase uuid");
    }
    return value;
}
/** `<ns>-cust-<customerId>`. */
export function customerStreamUserId(namespace, customerId) {
    return `${messagingNamespace(namespace)}-cust-${uuidOf("customerId", customerId)}`;
}
/** `<ns>-staff-<staffUserId>`. */
export function staffStreamUserId(namespace, staffUserId) {
    return `${messagingNamespace(namespace)}-staff-${uuidOf("staffUserId", staffUserId)}`;
}
/** `<ns>-lafayette-ai`: the author of AI replies. */
export function aiStreamUserId(namespace) {
    return `${messagingNamespace(namespace)}-lafayette-ai`;
}
/** `<ns>-lafayette-system`: the author of customer-safe notices. Also the creator of every customer channel. */
export function systemStreamUserId(namespace) {
    return `${messagingNamespace(namespace)}-lafayette-system`;
}
/** `<ns>-lafayette-copilot`: only in `staff_copilot` channels. */
export function copilotStreamUserId(namespace) {
    return `${messagingNamespace(namespace)}-lafayette-copilot`;
}
/** Reads a Stream user id. `null` for any id that this scheme did not build (including a Stream default id). */
export function parseStreamUserId(value) {
    const match = typeof value === "string" ? STREAM_USER_SHAPE.exec(value) : null;
    const groups = match?.groups;
    if (groups === undefined)
        return null;
    const namespace = groups.ns;
    if (groups.kind === "cust" || groups.kind === "staff") {
        return {
            namespace,
            kind: groups.kind === "cust" ? "customer" : "staff",
            lafayetteId: groups.id,
        };
    }
    return { namespace, kind: groups.actor, lafayetteId: null };
}
// --- channels --------------------------------------------------------------------------------------------------------
/** `<ns>-conv-<conversationId>`: the one `messaging` channel of a conversation. 45 characters at most for a 3-letter namespace. */
export function customerChannelId(namespace, conversationId) {
    return `${messagingNamespace(namespace)}-conv-${uuidOf("conversationId", conversationId)}`;
}
export function parseCustomerChannelId(value) {
    const groups = typeof value === "string" ? CUSTOMER_CHANNEL_SHAPE.exec(value)?.groups : undefined;
    if (groups === undefined)
        return null;
    return { namespace: groups.ns, conversationId: groups.id };
}
/**
 * `<ns>-cop-<hex32>`: the private channel of one staff member about one conversation. `hex32` is the first 32 hex characters
 * of sha256 of `"<staffUserId>:<conversationId>"`, so no table is needed and the id reveals nothing.
 */
export function copilotChannelId(namespace, staffUserId, conversationId) {
    const hash = sha256HexOfText(`${uuidOf("staffUserId", staffUserId)}:${uuidOf("conversationId", conversationId)}`);
    return `${messagingNamespace(namespace)}-cop-${hash.slice(0, 32)}`;
}
export function parseCopilotChannelId(value) {
    const groups = typeof value === "string" ? COPILOT_CHANNEL_SHAPE.exec(value)?.groups : undefined;
    return groups === undefined ? null : { namespace: groups.ns };
}
// --- messages --------------------------------------------------------------------------------------------------------
/** `lfai-<replyId>`: the one AI message of a reply. `replyId` is `conversation_replies.id`, which is `agent_runs.id`. */
export function aiReplyMessageId(replyId) {
    return `lfai-${uuidOf("replyId", replyId)}`;
}
/** `lfsys-<noticeId>`: a system notice. `noticeId` is deterministic per domain event, so a retry cannot post twice. */
export function systemNoticeMessageId(noticeId) {
    return `lfsys-${uuidOf("noticeId", noticeId)}`;
}
/** `lfcop-<uuid>`: a copilot message. */
export function copilotMessageId(messageUuid) {
    return `lfcop-${uuidOf("messageUuid", messageUuid)}`;
}
/**
 * Reads a message id. `client` is a lowercase uuid, which is what the customer app and the staff web set. Any other shape
 * (for example the SDK default `<userId>-<uuid>`) gives `null`: such a message never triggers the AI.
 */
export function parseMessageId(value) {
    const groups = typeof value === "string" ? MESSAGE_SHAPE.exec(value)?.groups : undefined;
    if (groups === undefined)
        return null;
    const kind = groups.prefix === "lfai"
        ? "ai"
        : groups.prefix === "lfsys"
            ? "system"
            : groups.prefix === "lfcop"
                ? "copilot"
                : "client";
    return { kind, uuid: groups.id };
}
/**
 * Deterministic uuid of a system notice from its domain event, so a retry derives the same id (`systemNoticeMessageId`).
 * Lane C passes the handoff id and the version of the event (for example `<handoffId>:<version>`).
 */
export function noticeIdFromEvent(eventKey) {
    if (typeof eventKey !== "string" || eventKey.length < 1 || eventKey.length > 200) {
        throw new DomainValidationError("eventKey", "must be 1 to 200 characters");
    }
    const hex = sha256HexOfText(`lafayette-system-notice:${eventKey}`);
    // A version 5 style uuid from the hash, so the text is a valid canonical uuid.
    const variant = ((Number.parseInt(hex.slice(16, 17), 16) & 0x3) | 0x8).toString(16);
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-5${hex.slice(13, 16)}-${variant}${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}
